import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import piexif from 'piexifjs';

import doFetch from '../../api/doFetch';
import useDealer from '../../api/useDealer';
import useVehicle from '../../api/useVehicle';
import { arrayMove } from '../../utils/arrayMove';
import { ControlBarContext } from '../ControlBarContext';
import VehiclePhotos from './components/vehicles/VehiclePhotos';
import VehicleInfoBoard from './components/vehicles/VehicleInfoBoard';
import VehicleAddPhotoModal from './components/vehicles/VehicleAddPhotoModal';

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 482px;
  width: 482px;
  max-height: calc(100vh - 151px);
  margin: 25px 10px 0 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 15px;
  & > div {
    overflow: unset;
    min-height: unset;
    max-height: unset;
    margin: 0px;
  }
`;

const VehicleWrapper = styled.div`
  display: flex;
  width: 100%;
`;

let lastVehicleRank = null;

const addUserComment = (file, comment) =>
  new Promise((resolve) => {
    if (file.type !== 'image/jpeg') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const jpeg = e.target.result;
      const exifObj = piexif.load(jpeg);
      const prefix = 'ASCII\0\0\0';
      exifObj.Exif[piexif.ExifIFD.UserComment] = prefix + comment;

      const exifBytes = piexif.dump(exifObj);
      const newData = piexif.insert(exifBytes, jpeg);

      const blob = dataURLtoBlob(newData);
      const newFile = new File([blob], file.name, { type: 'image/jpeg' });

      resolve(newFile);
    };

    reader.readAsDataURL(file);
  });

const dataURLtoBlob = (dataurl) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

const DealerVehicle = ({ getBreadCrumbs }) => {
  const { setBreadCrumbs } = useContext(ControlBarContext);
  const { hmsId, vin } = useParams();
  const { dealer } = useDealer(hmsId);
  const { vehicle, mutateVehicle } = useVehicle(hmsId, vin);
  const [vehicleImages, setVehicleImages] = useState([]);
  const [isUploadPhotosOpen, setIsUploadPhotosOpen] = useState(false);

  const toggleUploadModal = () => setIsUploadPhotosOpen(!isUploadPhotosOpen);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const temp = arrayMove(vehicleImages, oldIndex, newIndex).map((img, index) => ({
      ...img,
      rank: index
    }));
    setVehicleImages(temp);
    updatePhotosSort(temp);
  };

  const updatePhotosSort = (data) => {
    doFetch(`/vehicles/dealership/${hmsId}/vehicle/${vin}/images`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
      .then(() => {})
      .catch((error) => console.log(error));
  };

  const uploadImage = (img, id) => {
    const body = new FormData();
    body.append('image', img, img.name);
    return doFetch(
      `/vehicles/dealership/${hmsId}/vehicle/${vin}/images/${id}`,
      {
        method: 'POST',
        body
      },
      {}
    );
  };

  const prepareS3ForUpload = (imagesList) => {
    return doFetch(`/vehicles/dealership/${hmsId}/vehicle/${vin}/images`, {
      method: 'POST',
      body: JSON.stringify(imagesList)
    });
  };

  const handleUploadImages = async (images) => {
    try {
      const imagesWithExif = await Promise.all(
        images.map((img) => addUserComment(img, process.env.REACT_APP_CAPTURE_CODE || ''))
      );

      const imagesList = await prepareS3ForUpload(
        images.map((img, i) => ({ name: img.name, rank: lastVehicleRank + i }))
      );
      const image = await Promise.all(
        imagesList
          .filter((img) => !img?.url)
          .map((image, index) => uploadImage(imagesWithExif[index], image.id))
      );

      setVehicleImages(vehicleImages.concat(image));
      setIsUploadPhotosOpen(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeletePhotos = (items) => {
    doFetch(`/vehicles/dealership/${hmsId}/vehicle/${vin}/images`, {
      method: 'DELETE',
      body: JSON.stringify({ ids: items.map((item) => item.id) })
    })
      .then(() => mutateVehicle())
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    lastVehicleRank = vehicleImages?.length;
  }, [vehicleImages.length]);

  useEffect(() => {
    dealer && vehicle && setBreadCrumbs(getBreadCrumbs({ dealer, stock: vehicle?.stock }));
    vehicle?.images &&
      setVehicleImages(
        vehicle.images
          // .filter((img) => img.url)
          .sort((a, b) => a.rank - b.rank)
      );
  }, [getBreadCrumbs, setBreadCrumbs, vehicle, dealer]);

  return (
    <>
      <VehicleWrapper>
        <VehiclePhotos
          items={vehicleImages}
          onSortEnd={onSortEnd}
          openPhotoUploadModal={toggleUploadModal}
          editable={vehicle && vehicle.imageType === 'HomeNet'}
          handleDeletePhotos={handleDeletePhotos}
          vehicle={vehicle}
        />
        <RightSide>
          <VehicleInfoBoard vehicle={vehicle} mutateVehicle={mutateVehicle} />
        </RightSide>
      </VehicleWrapper>
      <VehicleAddPhotoModal
        isOpen={isUploadPhotosOpen}
        toggleHandler={toggleUploadModal}
        uploadImages={handleUploadImages}
      />
    </>
  );
};

export default DealerVehicle;
